import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, Copy, Eye, Mail, UserCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import ModuleAccessDenied from '../../components/ui/ModuleAccessDenied';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import complianceService from '../../services/compliance';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { FINDING_TYPE_LABELS, type AuditFinding, type EngagementDetail as Detail } from '../../types/compliance';

/**
 * /compliance/audits/:id — one auditor's access: its scope, what they have
 * opened (every view is logged), and the findings they have raised, each of
 * which the org answers and can turn into a corrective action.
 */

const ACTION_LABELS: Record<string, string> = {
  sign_in: 'Signed in',
  view_report: 'Opened the records',
  view_section: 'Viewed a section',
  download_pdf: 'Downloaded the PDF',
  download_csv: 'Downloaded the spray register',
  add_finding: 'Recorded a finding',
  withdraw_finding: 'Withdrew a finding',
};

function FindingCard({ f, engagementId, canRaise, tz, onChanged }: {
  f: AuditFinding; engagementId: number; canRaise: boolean; tz: string; onChanged: () => void;
}) {
  const [response, setResponse] = useState(f.org_response ?? '');
  const [raise, setRaise] = useState(false);
  const [due, setDue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const major = f.finding_type === 'nonconformity_major' || f.finding_type === 'nonconformity_minor';

  const save = async (status?: 'open' | 'closed') => {
    setSaving(true);
    setError(null);
    try {
      await complianceService.respondToFinding(engagementId, f.id, {
        org_response: response.trim() || null, status: status ?? null, raise_action: raise, due_date: raise ? due || null : null,
      });
      setRaise(false);
      onChanged();
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className={`rounded-lg border p-3 ${major && f.status === 'open' ? 'border-red-200 bg-red-50' : 'border-neutral-200'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-neutral-900">
          {FINDING_TYPE_LABELS[f.finding_type]}{f.clause_ref ? ` · ${f.clause_ref}` : ''}
        </p>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${f.status === 'open' ? 'bg-amber-100 text-amber-800' : 'bg-neutral-100 text-neutral-600'}`}>{f.status}</span>
      </div>
      <p className="mt-1 whitespace-pre-line break-words text-sm text-neutral-800">{f.finding}</p>
      <p className="mt-1 text-xs text-neutral-500">Raised {formatDateTimeInTz(f.created_at, tz)}</p>
      <textarea aria-label="Your response" className="mt-2 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" rows={2}
        value={response} onChange={e => setResponse(e.target.value)} placeholder="Your response — the auditor sees this" maxLength={4000} />
      {f.whs_action_id ? (
        <p className="mt-1 text-xs text-green-800">A corrective action has been raised in the WHS register.</p>
      ) : canRaise ? (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={raise} onChange={e => setRaise(e.target.checked)} />Raise a corrective action</label>
          {raise && <label className="inline-flex items-center gap-2">Due <input type="date" className="rounded-lg border border-neutral-300 px-2 py-1 text-sm" value={due} onChange={e => setDue(e.target.value)} /></label>}
        </div>
      ) : null}
      {error && <p className="mt-1 text-sm text-red-700">{error}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => save()} disabled={saving}>Save response</Button>
        {f.status === 'open'
          ? <Button size="sm" variant="outline" onClick={() => save('closed')} disabled={saving}>Save and close</Button>
          : <Button size="sm" variant="ghost" onClick={() => save('open')} disabled={saving}>Reopen</Button>}
      </div>
    </li>
  );
}

export default function EngagementDetail() {
  const { id } = useParams();
  const tz = useOrgTimezone();
  const spray = useModuleLicense('spray', 'admin');
  const hr = useModuleLicense('timesheet', 'hr');
  const jobs = useModuleLicense('farm_jobs', ['admin', 'supervisor']);
  const licenceLoading = spray.loading || hr.loading || jobs.loading;
  const anySeat = spray.hasAccess || hr.hasAccess || jobs.hasAccess;

  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setDetail(await complianceService.getEngagement(Number(id))); }
    catch (e) { setError(errorMessage(e, 'Could not load this access')); }
  }, [id]);
  useEffect(() => { if (!licenceLoading && anySeat) load(); }, [licenceLoading, anySeat, load]);

  if (licenceLoading) return <div className="mx-auto max-w-4xl px-4 py-6"><div className="h-40 animate-pulse rounded-xl bg-neutral-100" /></div>;
  if (!anySeat) return <ModuleAccessDenied moduleName="Compliance" />;

  const e = detail?.engagement;
  const live = e && (e.status === 'active' || e.status === 'scheduled');

  const revoke = async () => {
    if (!e || !window.confirm(`End ${e.auditor_name}'s access now? They will be signed out on their next click.`)) return;
    setBusy(true);
    try { await complianceService.revokeEngagement(e.id); setNotice('Access ended.'); await load(); }
    catch (err) { setError(errorMessage(err, 'Could not end access')); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    if (!e) return;
    setBusy(true);
    try {
      const out = await complianceService.resendEngagementLink(e.id);
      setNotice(out.email_sent ? 'A new link has been emailed. The old one no longer works.' : `The email could not be sent. New link (the old one no longer works): ${out.link}`);
    } catch (err) { setError(errorMessage(err, 'Could not send a new link')); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title={e?.title ?? 'Auditor access'} subtitle={e ? `${e.auditor_name}${e.auditor_company ? `, ${e.auditor_company}` : ''}` : undefined} icon={<UserCheck className="h-6 w-6 text-white" />} />
      <div className="mx-auto max-w-4xl space-y-5 px-4 py-6">
        <Link to="/compliance?tab=auditors" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"><ArrowLeft className="h-4 w-4" />Auditor access</Link>
        {error && <p className="text-sm text-red-700">{error}</p>}
        {notice && <p className="break-words rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">{notice}</p>}
        {!detail ? <div className="h-40 animate-pulse rounded-xl bg-neutral-100" /> : e && (
          <>
            <section className="rounded-xl border border-neutral-200 bg-white p-4">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div><dt className="text-neutral-500">Status</dt><dd className="font-medium capitalize">{e.status}</dd></div>
                <div className="min-w-0"><dt className="text-neutral-500">Sign-in code goes to</dt><dd className="break-all">{e.auditor_email}</dd></div>
                <div><dt className="text-neutral-500">Records from</dt><dd>{formatLocalDateInTz(e.period_start, tz)} – {formatLocalDateInTz(e.period_end, tz)}</dd></div>
                <div><dt className="text-neutral-500">Access until</dt><dd>{formatDateTimeInTz(e.access_until, tz)}</dd></div>
                <div><dt className="text-neutral-500">Sections</dt><dd className="capitalize">{e.sections.join(', ')}{e.pseudonymised ? ' · names hidden' : ''}</dd></div>
                <div><dt className="text-neutral-500">Last seen</dt><dd>{e.last_seen_at ? formatDateTimeInTz(e.last_seen_at, tz) : 'Not yet'}</dd></div>
              </dl>
              {live && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={resend} disabled={busy}><Mail className="mr-1 h-4 w-4" />Send a new link</Button>
                  <Button size="sm" variant="danger" onClick={revoke} disabled={busy}><Ban className="mr-1 h-4 w-4" />End access now</Button>
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-2 text-base font-semibold text-neutral-900">Findings ({detail.findings.length})</h2>
              {detail.findings.length === 0 ? <p className="text-sm text-neutral-500">The auditor has not recorded any findings.</p> : (
                <ul className="space-y-3">
                  {detail.findings.map(f => (
                    <FindingCard key={`${f.id}:${f.responded_at ?? ''}`} f={f} engagementId={e.id} canRaise={hr.hasAccess} tz={tz} onChanged={load} />
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-neutral-900"><Eye className="h-4 w-4" />What they have looked at</h2>
              {detail.log.length === 0 ? <p className="text-sm text-neutral-500">Nothing yet.</p> : (
                <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 text-sm">
                  {detail.log.map((l, i) => (
                    <li key={i} className="flex flex-col gap-0.5 p-2 sm:flex-row sm:justify-between">
                      <span>{ACTION_LABELS[l.action] ?? l.action}{typeof l.detail?.section === 'string' ? `: ${l.detail.section}` : ''}</span>
                      <span className="text-xs text-neutral-500">{formatDateTimeInTz(l.at, tz)}{l.ip_address ? ` · ${l.ip_address}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <p className="flex items-center gap-1 text-xs text-neutral-500"><Copy className="h-3.5 w-3.5" />The link is never shown again after it is sent; “Send a new link” replaces it.</p>
          </>
        )}
      </div>
    </>
  );
}
